import React, { memo } from 'react';
import { DXG } from '../../../../components/devexpress/Grid/dx-grid.ts';
import pageStyles from '../WarrantyRequest.module.scss';
import clsx from 'clsx';
import gridStyles from '../../../../components/devexpress/Grid/grid.module.scss';
import formStyles from '../../../../components/form/form.module.scss';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import { IconButton } from '../../../../components/uikit/IconButton.tsx';
import EditIcon from '../../../../assets/icons/edit.svg?react';
import ResetIcon from '../../../../assets/icons/reset.svg?react';
import CancelIcon from '../../../../assets/icons/close-button-small.svg?react';

import {
  AppPagination,
  AppPaginationProps,
} from '../../../../components/uikit/pagination/AppPagination.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import dxDataGrid, { OptionChangedEvent } from 'devextreme/ui/data_grid';
import { StatusFailed } from '../../warranty-request-history/components/Accordion.tsx';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { Trans } from 'react-i18next';
import { AppLink } from '../../../../components/uikit/buttons/AppLink.tsx';
import { Links } from '../../../../application/constants/links.ts';

type OwnProps = {
  dataGridRef?: React.RefObject<DataGridRef<any, number>>;
  changeItemRemoveStatus?: (itemNumber: string, isStored: boolean) => void;
  data: Record<string, any>[] | undefined;
  handleOptionChange?: (e: OptionChangedEvent) => void;
  columnsConfig: (IColumnProps & { disableExport?: boolean })[];
  desc: boolean;
  showRemoveItemFromCartModal?: (cellData: Record<string, string>) => void;
  showEditPartModal?: (cellInfo: Record<string, string>) => void;
  paginationProps: AppPaginationProps;
  tableInstance?: dxDataGrid<any, number>;
  totalRowName?: string;
  sortBy?: string | null;
  isWarrantyRequestPage?: boolean;
  customTableClassname?: string;
};
export const StoredValue = 'icon:garbage';

export const Table = memo(
  (props: OwnProps) => {
    const i18n = useScopedTranslation('Page.WarrantyPage');

    const {
      changeItemRemoveStatus,
      data,
      dataGridRef,
      paginationProps,
      columnsConfig,
      desc,
      showRemoveItemFromCartModal, // use it when it is possible to completely remove the item
      sortBy,
      handleOptionChange,
      tableInstance,
      showEditPartModal,
      totalRowName,
      isWarrantyRequestPage,
      customTableClassname,
    } = props;

    return (
      <div>
        <DXG.DataGrid
          loadPanel={{ enabled: false }}
          groupPanel={{ allowColumnDragging: false, visible: false }}
          grouping={{
            contextMenuEnabled: false,
            allowCollapsing: true,
          }}
          noDataText={''}
          ref={dataGridRef}
          dataSource={data}
          onRowPrepared={(e) => {
            if (e.rowType === 'data' && e.data.storno === StoredValue) {
              e.rowElement.classList.add(pageStyles.disabled);
            }

            if (
              !isWarrantyRequestPage &&
              e.values &&
              e.values.at(-1) === StatusFailed
            ) {
              e.rowElement.classList.add(pageStyles.statusRejected);
            }

            if (
              typeof e.key === 'object' &&
              data?.length === 1 &&
              e.key[0] === totalRowName
            ) {
              const expandElement =
                e.rowElement.querySelector('.dx-command-expand');
              if (expandElement) {
                expandElement.classList.add(pageStyles.hidden);
              }
            }
          }}
          className={clsx(
            gridStyles.editorOutline,
            formStyles.table,
            customTableClassname,
          )}
          onOptionChanged={handleOptionChange}
          sorting={{ mode: 'single' }}
          paging={{ pageSize: 150 }}
          onContextMenuPreparing={onContextMenuPreparing}
        >
          <Scrolling columnRenderingMode={'virtual'} showScrollbar={'always'} />
          {columnsConfig.map((x, index) => {
            const sortByField = sortBy || 'tartnr';
            return (
              <DXG.Column
                cssClass={clsx(x.cssClass, formStyles.text)}
                key={index}
                sortOrder={
                  x.dataField === sortByField
                    ? desc
                      ? 'desc'
                      : 'asc'
                    : undefined
                }
                showEditorAlways={x.allowEditing}
                headerCellRender={(cellInfo) => (
                  <TableHeaderCell
                    dataField={cellInfo.column.dataField}
                    caption={cellInfo.column.caption}
                    sortBy={sortBy}
                    desc={desc}
                  />
                )}
                {...x}
              />
            );
          })}
          {isWarrantyRequestPage && (
            <DXG.Column
              cssClass={formStyles.textCellPadding}
              width={120}
              cellRender={(cellInfo) => {
                const isStored = cellInfo.data.storno === StoredValue;
                return (
                  <>
                    {!isStored && (
                      <div
                        className={clsx(
                          clsx(
                            pageStyles.rowContainer24,
                            pageStyles.marginLeft28,
                          ),
                        )}
                      >
                        <IconButton
                          onClick={() => showEditPartModal?.(cellInfo.data)}
                          type={'primary'}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            changeItemRemoveStatus?.(cellInfo.data.epos, true)
                          }
                          type={'destroy'}
                        >
                          <CancelIcon />
                        </IconButton>
                      </div>
                    )}
                    {isStored && (
                      <div className={clsx(pageStyles.rowContainer24)}>
                        <div className={pageStyles.removedText}>
                          {i18n.t('table_removed')}
                        </div>
                        <div>
                          <IconButton
                            onClick={() => {
                              changeItemRemoveStatus?.(
                                cellInfo.data.epos,
                                false,
                              );
                            }}
                            type={'secondary'}
                          >
                            <ResetIcon />
                          </IconButton>
                        </div>
                      </div>
                    )}
                  </>
                );
              }}
            />
          )}
          <Pager visible={false} />
          <DXG.Editing
            mode="cell"
            allowUpdating={true}
            allowAdding={false}
            allowDeleting={false}
          />
        </DXG.DataGrid>
        {!!data?.length && isWarrantyRequestPage && (
          <>
            <AppPagination {...paginationProps} />
            <div className={clsx(pageStyles.greyText, pageStyles.linkToInfo)}>
              <Trans
                i18nKey={'Page.WarrantyPage.placeholder_text'}
                components={{
                  a: (
                    <AppLink
                      to={Links.Authorized.WarrantyInfo.route}
                      className={clsx(
                        pageStyles.underline,
                        pageStyles.greyText,
                      )}
                    />
                  ),
                }}
              />
            </div>
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.sortBy === nextProps.sortBy &&
      prevProps.tableInstance === nextProps.tableInstance &&
      prevProps.paginationProps === nextProps.paginationProps &&
      prevProps.desc === nextProps.desc &&
      prevProps.data === nextProps.data &&
      prevProps.dataGridRef === nextProps.dataGridRef &&
      prevProps.columnsConfig === nextProps.columnsConfig
    );
  },
);
